import type {
  MetaResponseModel,
  AutocompleteResponseModel,
  AutocompleteResponseModelAllOfAutocomplete,
} from '@searchspring/snapi-types'

import BaseSearchSpringController from '../base_searchspring_controller'
import { SearchspringUiRenderer } from '../../helpers/searchspring_ui_renderer'
import { SearchspringProduct } from '../../types/searchspring'

export default class SearchDrawerController extends BaseSearchSpringController {
  static targets = ['input', 'results', 'suggestions']

  declare readonly inputTarget: HTMLInputElement
  declare readonly resultsTarget: HTMLElement
  declare readonly suggestionsTarget: HTMLElement
  declare readonly hasResultsTarget: boolean
  declare readonly hasSuggestionsTarget: boolean

  private debounceTimer: ReturnType<typeof setTimeout> | null = null
  private readonly DEBOUNCE_MS = 300
  private trendingQueries: string[] = []

  connect() {
    super.connect()
    this.fetchTrending()
  }

  onInput() {
    if (this.debounceTimer) clearTimeout(this.debounceTimer)

    const query = this.inputTarget.value.trim()

    if (!query) {
      this.clearResults()
      this.showTrending()
      return
    }

    this.debounceTimer = setTimeout(
      () => this.fetchAutocomplete(),
      this.DEBOUNCE_MS,
    )
  }

  async fetchTrending() {
    try {
      const trendingResponse = await this.client.trending({
        limit: 3,
      })

      this.trendingQueries = (trendingResponse.trending?.queries ?? [])
        .map((q: { searchQuery?: string }) => q.searchQuery ?? '')
        .filter(Boolean)

      this.showTrending()
    } catch {
      this.trendingQueries = []
    }
  }

  private showTrending() {
    if (!this.hasSuggestionsTarget || this.trendingQueries.length === 0) return

    this.renderSuggestionList('Top Suggestions', this.trendingQueries)
  }

  async search(event: Event) {
    event.preventDefault()
    if (this.debounceTimer) clearTimeout(this.debounceTimer)

    const query = this.inputTarget.value.trim()
    if (!query) return

    const form = (event.target as HTMLElement).closest('form')
    const action = form?.action ?? '/search'

    window.location.href = `${action}?q=${encodeURIComponent(query)}`
  }

  async fetchAutocomplete() {
    const query = this.inputTarget.value.trim()

    if (!query) {
      this.clearResults()
      return
    }

    try {
      const [, autocomplete]: [MetaResponseModel, AutocompleteResponseModel] =
        await this.client.autocomplete({
          suggestions: { count: 10 },
          search: {
            query: { string: query, spellCorrection: true },
          },
        })

      this.renderSuggestions(autocomplete)
      this.renderResults(autocomplete)
    } catch {
      this.clearResults()
      if (this.hasSuggestionsTarget) {
        this.suggestionsTarget.innerHTML = ''
      }
    }
  }

  renderSuggestions(autocomplete: AutocompleteResponseModel) {
    if (!this.hasSuggestionsTarget) return

    const autocompleteResponse: AutocompleteResponseModelAllOfAutocomplete =
      autocomplete.autocomplete ?? {}

    const suggestions = autocompleteResponse.suggested?.text
      ? [
          autocompleteResponse.suggested,
          ...(autocompleteResponse.alternatives ?? []),
        ]
      : (autocompleteResponse.alternatives ?? [])

    const texts = suggestions.map((s) => s.text ?? '').filter(Boolean)

    if (texts.length === 0) return

    this.renderSuggestionList('Search Suggestions', texts)
  }

  private renderSuggestionList(title: string, items: string[]) {
    this.suggestionsTarget.innerHTML = ''

    const heading = document.createElement('p')
    heading.className = 'text-body text-primary/50 mb-3'
    heading.textContent = title

    const ul = document.createElement('ul')
    ul.className = 'search-suggestions'

    for (const text of items) {
      const li = document.createElement('li')
      li.className = 'mb-2 last:mb-0'

      const button = document.createElement('button')
      button.type = 'button'
      button.className =
        'text-body text-primary underline underline-offset-4 decoration-[2px] hover:text-primary/50 transition-opacity duration-200 disabled:text-primary/20'
      button.dataset['action'] = 'click->search-drawer#selectSuggestion'
      button.dataset['query'] = text
      button.textContent = text
      li.appendChild(button)
      ul.appendChild(li)
    }

    this.suggestionsTarget.appendChild(heading)
    this.suggestionsTarget.appendChild(ul)
  }

  renderResults(autocomplete: AutocompleteResponseModel) {
    if (!this.hasResultsTarget) return

    const items = autocomplete.results ?? []
    const validItems = items.filter(
      (item): item is (typeof items)[number] & { id: string } =>
        item.id !== undefined,
    )

    if (validItems.length === 0) {
      this.resultsTarget.innerHTML = `<p>No results found.</p>`
      return
    }

    this.resultsTarget.innerHTML = SearchspringUiRenderer.renderProducts(
      validItems as unknown as SearchspringProduct[],
    )
  }

  selectSuggestion(event: Event) {
    event.preventDefault()

    const target = event.currentTarget as HTMLElement
    const query = target.dataset.query ?? ''

    this.hideSuggestions()

    this.inputTarget.value = query
    this.inputTarget.form?.requestSubmit()
  }

  private clearResults() {
    if (this.hasResultsTarget) this.resultsTarget.innerHTML = ''
    if (this.hasSuggestionsTarget) this.hideSuggestions()
  }

  private hideSuggestions() {
    if (!this.hasSuggestionsTarget) return
    this.suggestionsTarget.innerHTML = ''
  }
}
